import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { extractLogsService } from '../services/api';
import { ErrorState } from './ErrorMessage';
import { getErrorMessage } from '../utils/errors';
import { formatDateTime, formatNumber, formatMins } from '../utils/format';

function RunDetail() {
    const { runId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const slowThreshold = 1.0;

    useEffect(() => {
        loadRunDetail();
    }, [runId]);

    const loadRunDetail = async () => {
        try {
            setLoading(true);
            const result = await extractLogsService.getRunDetail(runId);
            setData(result);
            setError(null);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const getCellClass = (mins) => {
        if (mins < 0.01) return 'zero';
        if (mins >= slowThreshold) return 'slow';
        return '';
    };

    const getStatusClass = (status) => {
        switch(status) {
            case 'Complete': return 'complete';
            case 'Running': return 'running';
            case 'Failed': return 'failed';
            default: return '';
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <div className="loading-text">Loading run details...</div>
            </div>
        );
    }

    if (error) {
        return <ErrorState title={`Couldn't load run #${runId}`} message={error} onRetry={loadRunDetail} />;
    }

    if (!data) {
        return (
            <div className="error-container">
                <div className="error-icon">🔍</div>
                <div className="error-message">No data found for this run</div>
                <Link to="/runs" className="btn btn-ghost" style={{marginTop: '20px'}}>
                    ← Back to Extract Runs
                </Link>
            </div>
        );
    }

    const { header, details, totals } = data;

    return (
        <div className="run-detail">
            <Link to="/runs" className="back-link">
                ← Back to Extract Runs
            </Link>
            
            <div className="page-header">
                <h1>Run #{header.runID} - {header.facilityID}</h1>
                <span className={`status-badge ${getStatusClass(header.status)}`}>
                    {header.status}
                </span>
            </div>
            
            <div className="card">
                <div className="header-info">
                    <div className="info-item">
                        <span className="info-label">Run ID</span>
                        <span className="info-value">#{header.runID}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Facility</span>
                        <span className="info-value">{header.facilityID}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Type</span>
                        <span className="info-value">{header.isHistorical ? 'Historical' : 'Daily'}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Start Time</span>
                        <span className="info-value">{formatDateTime(header.startTime)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">End Time</span>
                        <span className="info-value">{formatDateTime(header.endTime)}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Duration</span>
                        <span className="info-value">{formatMins(header.totalMinutes)} min</span>
                    </div>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card info">
                    <div className="stat-value">{totals.licenseCount}</div>
                    <div className="stat-label">License Keys</div>
                </div>
                <div className="stat-card success">
                    <div className="stat-value">{formatNumber(totals.grandTotalRows)}</div>
                    <div className="stat-label">Total Rows</div>
                </div>
                <div className="stat-card warning">
                    <div className="stat-value">{formatMins(totals.grandTotalMins)}</div>
                    <div className="stat-label">Total Minutes</div>
                </div>
                <div className="stat-card error">
                    <div className="stat-value">{totals.errorCount}</div>
                    <div className="stat-label">Errors</div>
                </div>
            </div>

            {totals.errorCount > 0 && (
                <div className="alert alert-error">
                    ⚠️ {totals.errorCount} license key(s) had errors - highlighted in the table below
                </div>
            )}

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th rowSpan="2">License Key</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>ATB</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>ZRO</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>BIL</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>NOT</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>PMT</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>ADJ</th>
                            <th colSpan="2" style={{textAlign: 'center'}}>CHG</th>
                            <th rowSpan="2" className="text-right">Total</th>
                        </tr>
                        <tr>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                            <th className="text-right">Rows</th><th className="text-right">Min</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...details].sort((a, b) => (b.totalMins || 0) - (a.totalMins || 0)).map((row, index) => (
                            <tr key={index} className={row.hasErrors ? 'error-row' : ''}>
                                <td className="facility">{row.licenseKey}</td>
                                <td className={`text-right ${row.atB_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.atB_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.atB_Mins)}`}>{formatMins(row.atB_Mins)}</td>
                                <td className={`text-right ${row.zrO_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.zrO_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.zrO_Mins)}`}>{formatMins(row.zrO_Mins)}</td>
                                <td className={`text-right ${row.biL_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.biL_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.biL_Mins)}`}>{formatMins(row.biL_Mins)}</td>
                                <td className={`text-right ${row.noT_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.noT_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.noT_Mins)}`}>{formatMins(row.noT_Mins)}</td>
                                <td className={`text-right ${row.pmT_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.pmT_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.pmT_Mins)}`}>{formatMins(row.pmT_Mins)}</td>
                                <td className={`text-right ${row.adJ_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.adJ_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.adJ_Mins)}`}>{formatMins(row.adJ_Mins)}</td>
                                <td className={`text-right ${row.chG_Rows === 0 ? 'zero' : ''}`}>{formatNumber(row.chG_Rows)}</td>
                                <td className={`text-right ${getCellClass(row.chG_Mins)}`}>{formatMins(row.chG_Mins)}</td>
                                <td className="text-right"><strong>{formatMins(row.totalMins)}</strong></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="totals-row">
                            <td><strong>TOTALS</strong></td>
                            <td className="text-right">{formatNumber(totals.totalATBRows)}</td>
                            <td className="text-right">{formatMins(totals.totalATBMins)}</td>
                            <td className="text-right">{formatNumber(totals.totalZRORows)}</td>
                            <td className="text-right">{formatMins(totals.totalZROMins)}</td>
                            <td className="text-right">{formatNumber(totals.totalBILRows)}</td>
                            <td className="text-right">{formatMins(totals.totalBILMins)}</td>
                            <td className="text-right">{formatNumber(totals.totalNOTRows)}</td>
                            <td className="text-right">{formatMins(totals.totalNOTMins)}</td>
                            <td className="text-right">{formatNumber(totals.totalPMTRows)}</td>
                            <td className="text-right">{formatMins(totals.totalPMTMins)}</td>
                            <td className="text-right">{formatNumber(totals.totalADJRows)}</td>
                            <td className="text-right">{formatMins(totals.totalADJMins)}</td>
                            <td className="text-right">{formatNumber(totals.totalCHGRows)}</td>
                            <td className="text-right">{formatMins(totals.totalCHGMins)}</td>
                            <td className="text-right"><strong>{formatMins(totals.grandTotalMins)}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="summary">
                <div className="summary-item">
                    <span className="summary-label">Total Rows Extracted</span>
                    <span className="summary-value">{formatNumber(totals.grandTotalRows)}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Slow Threshold</span>
                    <span className="summary-value">{slowThreshold} min</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">License Keys Processed</span>
                    <span className="summary-value">{totals.licenseCount}</span>
                </div>
            </div>
        </div>
    );
}

export default RunDetail;